import React, { useMemo, useRef, useState } from "react";

import CountsTable from "../../components/CountsTable";
import ExportMenu from "../../components/ExportMenu";

import TrafficChart from "../../components/charts/TrafficChart";
import ChartExportMenu from "../../components/charts/ChartExportMenu";

import { firstOfMonth, startOfDay, endOfDay } from "../../lib/dates";
import { goSightings } from "../../lib/links";
import { toQueryCam } from "../../lib/cameras";
import { exportTable } from "../../lib/csv";

const navBtn = {
  padding: "4px 10px",
  borderRadius: 8,
  border: "1px solid #1f2834",
  background: "#151b24",
  color: "white",
  lineHeight: 1,
  fontSize: 16,
  cursor: "pointer",
};

export default function MonthCard({ month, setMonth, visible, rows, plate, setDay }) {
  const [stacked, setStacked] = useState(true);
  const chartRef = useRef(null);

  const card = { background: "#0f141c", border: "1px solid #1f2834", borderRadius: 16, padding: 16 };

  const chartData = useMemo(() => {
    const src = Array.isArray(rows) ? rows : [];
    return src.map((r) => ({
      label: r.day,
      k41: Number(r["41"] || 0),
      chel: Number(r["Cheltenham"] || 0),
      pool: Number(r["Pool"] || 0),
    }));
  }, [rows]);

  const visibleRowTotalFor = (r) =>
    (visible.cam41 ? Number(r["41"] ?? 0) : 0) +
    (visible.chel ? Number(r["Cheltenham"] ?? 0) : 0) +
    (visible.pool ? Number(r["Pool"] ?? 0) : 0);

  const buildExport = () => {
    const src = (rows || []).filter((r) => visibleRowTotalFor(r) > 0);
    const headers = [
      "day",
      ...(visible.cam41 ? ["41"] : []),
      ...(visible.chel ? ["Cheltenham"] : []),
      ...(visible.pool ? ["Pool"] : []),
      "Total",
    ];
    const out = src.map((r) => ({
      day: r.day,
      ...(visible.cam41 ? { "41": r["41"] } : {}),
      ...(visible.chel ? { Cheltenham: r["Cheltenham"] } : {}),
      ...(visible.pool ? { Pool: r["Pool"] } : {}),
      Total: visibleRowTotalFor(r),
    }));
    const title = `Traffic in ${month.toLocaleString(undefined, { month: "long", year: "numeric" })}`;
    const filename = `month_${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`;
    return { headers, rows: out, title, filename };
  };

  return (
    <section style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ margin: 0, color: "#9ec3ff", fontSize: 18 }}>
          Traffic in {month.toLocaleString(undefined, { month: "long", year: "numeric" })}
        </h2>
        <ExportMenu
          onExport={(t) => {
            const { headers, rows, title, filename } = buildExport();
            exportTable(t, headers, rows, title, filename);
          }}
        />
      </div>

      {/* Month navigation */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <div style={{ color: "#a7b3c4", fontSize: 12 }}>Month</div>
        <button
          style={navBtn}
          aria-label="Previous month"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
        >
          ‹
        </button>
        <input
          type="month"
          value={`${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`}
          onChange={(e) => {
            const [yy, mm] = e.target.value.split("-").map((x) => parseInt(x, 10));
            setMonth(new Date(yy, (mm || 1) - 1, 1));
          }}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #1f2834", background: "#0f141c", color: "white" }}
        />
        <button
          style={navBtn}
          aria-label="Next month"
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
        >
          ›
        </button>
        <button
          onClick={() => setMonth(firstOfMonth(new Date()))}
          style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #1f2834", background: "#151b24", color: "white" }}
        >
          This Month
        </button>
      </div>

      <CountsTable
        rows={rows}
        firstColKey="day"
        firstColHeader="Day / Camera"
        visible={visible}
        onFirstCol={(r) => setDay(startOfDay(new Date(r.day)))}
        onCell={(r, key) => {
          const d = new Date(r.day);
          const start = startOfDay(d);
          const end = endOfDay(d);
          const cams =
            key === "Total"
              ? [
                  ...(visible.cam41 ? ["41"] : []),
                  ...(visible.chel ? ["cheltenham"] : []),
                  ...(visible.pool ? ["pool"] : []),
                ]
              : [toQueryCam(key)];
          setDay(start);
          goSightings(start, end, cams, plate);
        }}
      />

      {/* Chart header: stacked/grouped + export */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
        <div style={{ color: "#a7b3c4", fontSize: 12 }}>Chart</div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select
            value={stacked ? "stacked" : "grouped"}
            onChange={(e) => setStacked(e.target.value === "stacked")}
            style={{ padding: "4px 8px", background: "#0f141c", color: "white", borderRadius: 6, border: "1px solid #1f2834" }}
          >
            <option value="stacked">Stacked</option>
            <option value="grouped">Grouped</option>
          </select>
          <ChartExportMenu
            targetRef={chartRef}
            filename={`month_${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, "0")}`}
          />
        </div>
      </div>

      <div ref={chartRef}>
        <TrafficChart data={chartData} visible={visible} stacked={stacked} height={160} />
      </div>
    </section>
  );
}
